'use client';

import { PublicPlace } from '../../types';

interface PlaceCardProps {
  place: PublicPlace;
  isSelected: boolean;
  onClick: () => void;
  /** Lieu incompatible avec la sélection courante : grisé + raison, non cliquable. */
  disabled?: boolean;
  disabledReason?: string;
}

const PlaceKindIcon = ({ kind }: { kind: PublicPlace['kind'] }) => {
  if (kind === 'ESTABLISHMENT') {
    return (
      <svg viewBox="0 0 24 24" className="bt-place-card__icon-svg" aria-hidden="true" focusable="false">
        <path
          d="M12 22s7-6.3 7-12a7 7 0 1 0-14 0c0 5.7 7 12 7 12zm0-9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"
          fill="currentColor"
        />
      </svg>
    );
  }

  if (kind === 'PHONE') {
    return (
      <svg viewBox="0 0 24 24" className="bt-place-card__icon-svg" aria-hidden="true" focusable="false">
        <path
          d="M7.2 3.8a1.4 1.4 0 0 1 1.7-.9l2.2.7c.6.2 1 .8.9 1.4l-.3 2a1.4 1.4 0 0 1-1 1.1l-1 .3a13.5 13.5 0 0 0 5.9 5.9l.3-1a1.4 1.4 0 0 1 1.1-1l2-.3c.7-.1 1.2.3 1.4.9l.7 2.2a1.4 1.4 0 0 1-.9 1.7l-1.5.5a3 3 0 0 1-2.3-.2 18.2 18.2 0 0 1-9.5-9.5 3 3 0 0 1-.2-2.3l.5-1.5z"
          fill="currentColor"
        />
      </svg>
    );
  }

  if (kind === 'VIDEO') {
    return (
      <svg viewBox="0 0 24 24" className="bt-place-card__icon-svg" aria-hidden="true" focusable="false">
        <path
          d="M3 8.5A2.5 2.5 0 0 1 5.5 6h8A2.5 2.5 0 0 1 16 8.5v7a2.5 2.5 0 0 1-2.5 2.5h-8A2.5 2.5 0 0 1 3 15.5v-7zm14.5 2 3.8-2.2A.5.5 0 0 1 22 8.7v6.6a.5.5 0 0 1-.7.4l-3.8-2.2v-3z"
          fill="currentColor"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="bt-place-card__icon-svg" aria-hidden="true" focusable="false">
      <path
        d="M12 2a10 10 0 1 0 10 10h-2a8 8 0 1 1-2.3-5.7L14.5 9.5H22V2l-2.9 2.9A9.9 9.9 0 0 0 12 2z"
        fill="currentColor"
      />
    </svg>
  );
};

export function PlaceCard({ place, isSelected, onClick, disabled, disabledReason }: PlaceCardProps) {
  return (
    <div
      className={`bt-place-card ${isSelected ? 'bt-place-card--selected' : ''} ${
        disabled ? 'bt-place-card--unavailable' : ''
      }`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="bt-place-card__icon">
        <PlaceKindIcon kind={place.kind} />
      </div>
      <div className="bt-place-card__info">
        <div className="bt-place-card__label">{place.label}</div>
        {place.address?.formatted_address && (
          <div className="bt-place-card__address">{place.address.formatted_address}</div>
        )}
        {disabled && disabledReason ? (
          <div className="bt-place-card__unavailable">{disabledReason}</div>
        ) : (
          place.google_reviews && (
            <div className="bt-place-card__reviews">
              ★ {place.google_reviews.rating.toFixed(1)} ({place.google_reviews.total_reviews} avis)
            </div>
          )
        )}
      </div>
    </div>
  );
}
